/**
 * Generazione PDF dello storico workout come AGGREGATO DI SCREENSHOT: un'immagine
 * per ogni giorno della scheda, catturata dal rendering reale della dashboard
 * (gli stessi componenti `WorkoutTables`), poi assemblata in un vero file .pdf
 * scaricato direttamente.
 *
 * - Cattura DOM → immagine con `html2canvas` (import dinamico: resta fuori dal
 *   bundle principale).
 * - Assemblaggio PDF con un writer JPEG→PDF minimale a ZERO dipendenze: si evita
 *   `jspdf` che (con la sua dipendenza `canvg`) rompe la build di react-scripts 5.
 */

type JpegPage = { bytes: Uint8Array; w: number; h: number };

/**
 * Cattura ogni elemento come immagine e produce/scarica un PDF con una pagina
 * per elemento (una per giorno).
 */
export async function captureElementsToPdf(
  elements: HTMLElement[],
  fileName: string
): Promise<void> {
  const html2canvas = (await import("html2canvas")).default;

  const pages: JpegPage[] = [];
  for (const el of elements) {
    // Disattiva temporaneamente l'overflow per non tagliare le settimane.
    const prevOverflow = el.style.overflow;
    el.style.overflow = "visible";
    let canvas: HTMLCanvasElement;
    try {
      canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
    } finally {
      el.style.overflow = prevOverflow;
    }
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    pages.push({
      bytes: dataUrlToBytes(dataUrl),
      w: canvas.width,
      h: canvas.height,
    });
  }

  if (pages.length === 0) {
    throw new Error("Nessuna immagine da esportare");
  }

  const blob = buildPdfFromJpegs(pages);
  downloadBlob(blob, fileName);
}

// --- Writer PDF (JPEG/DCTDecode), byte-accurate, zero dipendenze ---

function buildPdfFromJpegs(pages: JpegPage[]): Blob {
  const chunks: Uint8Array[] = [];
  let offset = 0;
  const offsets: Record<number, number> = {};

  const latin1 = (s: string): Uint8Array => {
    const u = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i) & 0xff;
    return u;
  };
  const push = (data: string | Uint8Array) => {
    const u8 = typeof data === "string" ? latin1(data) : data;
    chunks.push(u8);
    offset += u8.length;
  };
  const startObj = (num: number) => {
    offsets[num] = offset;
    push(num + " 0 obj\n");
  };
  const endObj = () => push("endobj\n");

  // Header (con marker binario, come da spec per file con dati binari).
  push("%PDF-1.3\n%\xE2\xE3\xCF\xD3\n");

  const N = pages.length;

  // obj 1: Catalog
  startObj(1);
  push("<< /Type /Catalog /Pages 2 0 R >>\n");
  endObj();

  // obj 2: Pages
  const kids: string[] = [];
  for (let i = 0; i < N; i++) kids.push(3 + i * 3 + " 0 R");
  startObj(2);
  push("<< /Type /Pages /Kids [" + kids.join(" ") + "] /Count " + N + " >>\n");
  endObj();

  const PAGE_W = 800; // punti; l'altezza pagina segue l'aspect ratio dell'immagine
  for (let i = 0; i < N; i++) {
    const p = pages[i];
    const pageNum = 3 + i * 3;
    const contentNum = pageNum + 1;
    const imgNum = pageNum + 2;
    const pw = PAGE_W;
    const ph = Math.max(1, Math.round((PAGE_W * p.h) / p.w));

    // Page
    startObj(pageNum);
    push(
      "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 " +
        pw +
        " " +
        ph +
        "] /Resources << /XObject << /Im0 " +
        imgNum +
        " 0 R >> >> /Contents " +
        contentNum +
        " 0 R >>\n"
    );
    endObj();

    // Content stream: disegna l'immagine a tutta pagina
    const content = "q " + pw + " 0 0 " + ph + " 0 0 cm /Im0 Do Q\n";
    startObj(contentNum);
    push("<< /Length " + content.length + " >>\nstream\n");
    push(content);
    push("endstream\n");
    endObj();

    // Image XObject (JPEG)
    startObj(imgNum);
    push(
      "<< /Type /XObject /Subtype /Image /Width " +
        p.w +
        " /Height " +
        p.h +
        " /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length " +
        p.bytes.length +
        " >>\nstream\n"
    );
    push(p.bytes);
    push("\nendstream\n");
    endObj();
  }

  // xref
  const totalObjs = 2 + N * 3;
  const xrefOffset = offset;
  let xref = "xref\n0 " + (totalObjs + 1) + "\n";
  xref += "0000000000 65535 f \n";
  for (let n = 1; n <= totalObjs; n++) {
    xref += String(offsets[n] || 0).padStart(10, "0") + " 00000 n \n";
  }
  push(xref);
  push(
    "trailer\n<< /Size " +
      (totalObjs + 1) +
      " /Root 1 0 R >>\nstartxref\n" +
      xrefOffset +
      "\n%%EOF"
  );

  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return new Blob([out], { type: "application/pdf" });
}

// --- Helpers ---

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(",")[1] || "";
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function sanitizeFileName(name: string): string {
  return (name || "scheda").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 60);
}
